import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, TrendingUp, ArrowRight, Loader2, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import {
  useGetDemandesRachatQuery,
  useSimulerRachatMutation,
  useApprouverLotMutation,
} from '../../services/rachatApi';
import type { SimulationEtudiant, SimulationUE, DemandeRachat } from '../../services/rachatApi';

interface Props {
  demandeIds: number[];
  onClose: () => void;
  onApproved: () => void;
}

/**
 * SimulationModal — UE-centric simulation with editable exam notes.
 *
 * Flow:
 * 1. Load demandes → build initial ajustements map (matiere_id → note_proposee)
 * 2. Call backend /simuler with ajustements → get per-student UE/ECUE before/after
 * 3. Jury can edit note_proposee in inputs → re-simulate on change
 * 4. "Confirmer" → approuver_lot with the selected demande_ids
 */
const SimulationModal: React.FC<Props> = ({ demandeIds, onClose, onApproved }) => {
  // Fetch the actual demandes to get matiere_id, etudiant_id, note_proposee
  const { data: allDemandes } = useGetDemandesRachatQuery({ statut: 'en_attente' });
  const demandes: DemandeRachat[] = useMemo(() => {
    const list = Array.isArray(allDemandes) ? allDemandes : (allDemandes as any)?.results ?? [];
    return list.filter((d: DemandeRachat) => demandeIds.includes(d.id));
  }, [allDemandes, demandeIds]);

  // Map: `${etudiant_id}_${matiere_id}` → note_proposee (editable)
  const [noteEdits, setNoteEdits] = useState<Record<string, number>>({});
  // Original proposed notes (from backend auto-proposal)
  const [originalNotes, setOriginalNotes] = useState<Record<string, number>>({});

  const [resultats, setResultats] = useState<SimulationEtudiant[]>([]);
  const [expandedEtud, setExpandedEtud] = useState<number | null>(null);

  const [simuler, { isLoading: simulating }] = useSimulerRachatMutation();
  const [approuverLot, { isLoading: approving }] = useApprouverLotMutation();

  // Initialize noteEdits from demandes
  useEffect(() => {
    if (demandes.length === 0) return;
    const edits: Record<string, number> = {};
    const originals: Record<string, number> = {};
    for (const d of demandes) {
      const key = `${d.etudiant}_${d.matiere}`;
      const val = parseFloat(d.note_proposee);
      edits[key] = val;
      originals[key] = val;
    }
    setNoteEdits(edits);
    setOriginalNotes(originals);
  }, [demandes]);

  // Build ajustements from noteEdits
  const ajustements = useMemo(() => {
    return demandes.map(d => ({
      matiere_id: d.matiere,
      etudiant_id: d.etudiant,
      note_proposee: noteEdits[`${d.etudiant}_${d.matiere}`] ?? parseFloat(d.note_proposee),
    }));
  }, [demandes, noteEdits]);

  // Run simulation
  const lancerSimulation = useCallback(async () => {
    if (ajustements.length === 0) return;
    try {
      const res = await simuler({ ajustements }).unwrap();
      setResultats(res.resultats);
      // Auto-expand first student
      if (res.resultats.length > 0 && expandedEtud === null) {
        setExpandedEtud(res.resultats[0].etudiant_id);
      }
    } catch {
      // silent
    }
  }, [ajustements, simuler]);

  // Simulate on mount and when notes change (debounced)
  useEffect(() => {
    if (Object.keys(noteEdits).length === 0) return;
    const timer = setTimeout(() => lancerSimulation(), 300);
    return () => clearTimeout(timer);
  }, [noteEdits, lancerSimulation]);

  // Handle note edit
  const handleNoteChange = (etudiantId: number, matiereId: number, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > 20) return;
    setNoteEdits(prev => ({
      ...prev,
      [`${etudiantId}_${matiereId}`]: Math.round(num * 100) / 100,
    }));
  };

  // Reset to auto-proposed
  const resetToOriginal = () => {
    setNoteEdits({ ...originalNotes });
  };

  // Approve
  const handleApprouver = async () => {
    try {
      await approuverLot({ demande_ids: demandeIds }).unwrap();
      onApproved();
    } catch {
      // error handled by RTK
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <TrendingUp size={20} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Simulation de Rachat
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({demandeIds.length} demande{demandeIds.length > 1 ? 's' : ''})
            </span>
            {simulating && <Loader2 size={16} className="animate-spin text-blue-500" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToOriginal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              title="Remettre les propositions automatiques"
            >
              <RotateCcw size={12} />
              Reset auto
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {resultats.length === 0 && !simulating && (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500">
              {demandes.length === 0 ? 'Chargement des demandes...' : 'Aucun résultat de simulation'}
            </div>
          )}

          {resultats.map(etud => {
            const isExpanded = expandedEtud === etud.etudiant_id;
            const semValid = etud.semestre_valide;

            return (
              <div key={etud.etudiant_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* Student header with semester before/after */}
                <button
                  onClick={() => setExpandedEtud(isExpanded ? null : etud.etudiant_id)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700/70"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{etud.etudiant_nom}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{etud.etudiant_matricule}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Semester average before → after */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Moy. Sem.</span>
                      <span className="font-mono text-sm text-red-600 dark:text-red-400">{etud.moy_semestre_avant.toFixed(2)}</span>
                      <ArrowRight size={14} className="text-gray-400" />
                      <span className={`font-mono text-sm font-bold ${
                        etud.moy_semestre_apres >= 10
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {etud.moy_semestre_apres.toFixed(2)}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      semValid
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {semValid ? 'Validé' : 'Non validé'}
                    </span>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>

                {/* Expanded: UE details with editable ECUEs */}
                {isExpanded && (
                  <div className="px-4 py-3 space-y-3">
                    {/* UE table */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                          <th className="text-left py-1 font-medium">UE</th>
                          <th className="text-center py-1 font-medium">Moy. Avant</th>
                          <th className="text-center py-1 font-medium">Moy. Après</th>
                          <th className="text-center py-1 font-medium">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {etud.ues.map((ue: SimulationUE) => {
                          const improved = ue.moyenne_apres > ue.moyenne_avant;
                          return (
                            <React.Fragment key={ue.code_ue}>
                              <tr className="border-t border-gray-100 dark:border-gray-700">
                                <td className="py-2">
                                  <span className="font-mono text-xs text-gray-600 dark:text-gray-300 mr-2">{ue.code_ue}</span>
                                  <span className="text-gray-700 dark:text-gray-300">{ue.nom_ue}</span>
                                </td>
                                <td className="py-2 text-center font-mono text-gray-500 dark:text-gray-400">{ue.moyenne_avant.toFixed(2)}</td>
                                <td className="py-2 text-center">
                                  <span className={`font-mono font-semibold ${
                                    improved ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'
                                  }`}>
                                    {ue.moyenne_apres.toFixed(2)}
                                  </span>
                                </td>
                                <td className="py-2 text-center">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                    ue.validee_apres
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  }`}>
                                    {ue.validee_apres ? 'Validée' : 'Non validée'}
                                  </span>
                                </td>
                              </tr>

                              {/* ECUE details with editable inputs */}
                              {ue.ecues && ue.ecues.length > 0 && ue.ecues.map(ecue => {
                                const key = `${etud.etudiant_id}_${ecue.matiere_id}`;
                                const currentVal = noteEdits[key];
                                const isModified = currentVal !== undefined && originalNotes[key] !== undefined && currentVal !== originalNotes[key];

                                return (
                                  <tr key={ecue.matiere_id} className="bg-gray-50/50 dark:bg-gray-700/20">
                                    <td className="py-1.5 pl-8">
                                      <span className="font-mono text-[11px] text-gray-400 mr-1">{ecue.code_matiere}</span>
                                      <span className="text-xs text-gray-600 dark:text-gray-400">{ecue.nom_matiere}</span>
                                    </td>
                                    <td className="py-1.5 text-center">
                                      <span className="font-mono text-xs text-gray-500">
                                        {ecue.note_examen_avant !== null ? ecue.note_examen_avant.toFixed(2) : '—'}
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-center">
                                      {currentVal !== undefined ? (
                                        <input
                                          type="number"
                                          min={4}
                                          max={9.99}
                                          step={0.5}
                                          value={currentVal}
                                          title="Plage de rachat : 4 à 9,99 (en dessous = irrécupérable, ≥ 10 = pas besoin de rachat)"
                                          onChange={e => handleNoteChange(etud.etudiant_id, ecue.matiere_id, e.target.value)}
                                          className={`w-20 text-center font-mono text-xs px-2 py-1 rounded border ${
                                            isModified
                                              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                          }`}
                                        />
                                      ) : (
                                        <span className="font-mono text-xs text-gray-500">
                                          {ecue.note_examen_apres !== null ? ecue.note_examen_apres.toFixed(2) : '—'}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1.5 text-center">
                                      {ecue.moyenne_ecue_avant !== null && ecue.moyenne_ecue_apres !== null && (
                                        <span className="text-[10px] text-gray-400">
                                          ECUE: {ecue.moyenne_ecue_avant.toFixed(2)} → {ecue.moyenne_ecue_apres.toFixed(2)}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Les notes d'examen modifiables sont éditables ci-dessus. La simulation recalcule en temps réel.
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              Annuler
            </button>
            <button
              onClick={handleApprouver}
              disabled={approving || demandeIds.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {approving ? (
                <><Loader2 size={16} className="animate-spin" /> Approbation...</>
              ) : (
                <>Confirmer l'approbation ({demandeIds.length})</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationModal;
